import React, { useState, useEffect } from 'react';
import { Building2, Mail, Phone, MapPin, Download, CheckCircle, XCircle, Clock, FileText, Eye } from 'lucide-react';
import axios from 'axios';
import FileViewerModal from './common/FileViewerModal';

interface AgentRegistration {
  id: number;
  agency_name: string;
  address: string;
  city?: string;
  country?: string;
  email: string;
  phone: string;
  business_card_s3_key?: string;
  registration_doc_s3_key?: string;
  logo_s3_key?: string;
  color_theme?: string;
  status: 'pending' | 'approved' | 'rejected' | 'disabled';
  rejection_reason?: string;
  reviewed_by?: number;
  reviewed_at?: string;
  created_at: string;
  updated_at?: string;
}

const AgentRegistrations: React.FC = () => {
  const [registrations, setRegistrations] = useState<AgentRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [rejectModal, setRejectModal] = useState<{ show: boolean; id: number | null; reason: string }>({
    show: false,
    id: null,
    reason: ''
  });
  const [registerModal, setRegisterModal] = useState<{ 
    show: boolean; 
    mode: 'register' | 'edit';
    agencies: any[]; 
    selectedAgency: any | null;
    editRegistration: AgentRegistration | null;
    formData: {
      agency_name: string;
      address: string;
      city: string;
      country: string;
      email: string;
      phone: string;
      color_theme: string;
    };
    businessCard: File | null;
    registrationDoc: File | null;
    logo: File | null;
  }>({
    show: false,
    mode: 'register',
    agencies: [],
    selectedAgency: null,
    editRegistration: null,
    formData: {
      agency_name: '',
      address: '',
      city: '',
      country: '',
      email: '',
      phone: '',
      color_theme: '#1E40AF'
    },
    businessCard: null,
    registrationDoc: null,
    logo: null
  });

  const [fileViewer, setFileViewer] = useState<{
    isOpen: boolean;
    fileUrl: string;
    fileName: string;
    fileType: 'pdf' | 'image';
  }>({
    isOpen: false,
    fileUrl: '',
    fileName: '',
    fileType: 'image'
  });



  const [agencySearch, setAgencySearch] = useState('');
  const [showAgencyDropdown, setShowAgencyDropdown] = useState(false); 
  const filteredAgencies = registerModal.agencies.filter(agency =>
    agency.agency_name.toLowerCase().includes(agencySearch.toLowerCase()) ||
    (agency.email && agency.email.toLowerCase().includes(agencySearch.toLowerCase()))
  );

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

  useEffect(() => {
    fetchRegistrations();
  }, [activeTab]);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/agent-registrations?status=${activeTab}`);
      setRegistrations(response.data);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const previewDocument = async (id: number, docType: 'business_card' | 'registration_doc' | 'logo') => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/agent-registrations/${id}/document/${docType}?preview=true`);
      const fileName = docType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
      const fileType = docType === 'registration_doc' ? 'pdf' : 'image';
      
      setFileViewer({
        isOpen: true,
        fileUrl: response.data.url,
        fileName,
        fileType
      });
    } catch (error) {
      console.error('Error previewing document:', error);
      alert('Failed to preview document');
    }
  };

  const downloadDocument = async (id: number, docType: 'business_card' | 'registration_doc' | 'logo') => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/agent-registrations/${id}/document/${docType}?preview=false`);
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  const handleApprove = async (id: number) => {
    if (!window.confirm('Approve this registration? This will create an agency account and send a welcome email.')) return;

    try {
      const formData = new FormData();
      formData.append('status', 'approved');
      
      await axios.post(`${API_URL}/api/admin/agent-registrations/${id}/review`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      alert('Registration approved successfully!');
      fetchRegistrations();
    } catch (error) {
      console.error('Error approving registration:', error);
      alert('Failed to approve registration');
    }
  };

  const handleReject = async () => {
    if (!rejectModal.id || !rejectModal.reason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('status', 'rejected');
      formData.append('rejection_reason', rejectModal.reason);
      
      await axios.post(`${API_URL}/api/admin/agent-registrations/${rejectModal.id}/review`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      alert('Registration rejected');
      setRejectModal({ show: false, id: null, reason: '' });
      fetchRegistrations();
    } catch (error) {
      console.error('Error rejecting registration:', error);
      alert('Failed to reject registration');
    }
  };

  const handleDisable = async (id: number) => {
    if (!window.confirm('Disable this registration? This will deactivate portal access.')) return;

    try {
      await axios.post(`${API_URL}/api/admin/agent-registrations/${id}/disable`);
      alert('Registration disabled successfully!');
      fetchRegistrations();
    } catch (error) {
      console.error('Error disabling registration:', error);
      alert('Failed to disable registration');
    }
  };

  const handleEnable = async (id: number) => {
    if (!window.confirm('Enable this registration? This will reactivate portal access.')) return;

    try {
      await axios.post(`${API_URL}/api/admin/agent-registrations/${id}/enable`);
      alert('Registration enabled successfully!');
      fetchRegistrations();
    } catch (error) {
      console.error('Error enabling registration:', error);
      alert('Failed to enable registration');
    }
  };

  const handleEdit = (id: number) => {
    const registration = registrations.find(r => r.id === id);
    if (registration) {
      setRegisterModal({
        show: true,
        mode: 'edit',
        agencies: [],
        selectedAgency: null,
        editRegistration: registration,
        formData: {
          agency_name: registration.agency_name,
          address: registration.address,
          city: registration.city || '',
          country: registration.country || '',
          email: registration.email,
          phone: registration.phone,
          color_theme: registration.color_theme || '#1E40AF'
        },
        businessCard: null,
        registrationDoc: null,
        logo: null
      });
    }
  };

  const handleUpdateRegistration = async () => {
    if (!registerModal.editRegistration) return;

    try {
      const formData = new FormData();
      formData.append('agency_name', registerModal.formData.agency_name);
      formData.append('address', registerModal.formData.address);
      formData.append('city', registerModal.formData.city);
      formData.append('country', registerModal.formData.country);
      formData.append('email', registerModal.formData.email);
      formData.append('phone', registerModal.formData.phone);
      formData.append('color_theme', registerModal.formData.color_theme);

      await axios.put(`${API_URL}/api/admin/agent-registrations/${registerModal.editRegistration.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      alert('Registration updated successfully!');
      setRegisterModal({ 
        show: false, 
        mode: 'register',
        agencies: [], 
        selectedAgency: null,
        editRegistration: null,
        formData: {
          agency_name: '',
          address: '',
          city: '',
          country: '',
          email: '',
          phone: '',
          color_theme: '#1E40AF'
        },
        businessCard: null, 
        registrationDoc: null,
        logo: null
      });
      fetchRegistrations();
    } catch (error) {
      console.error('Error updating registration:', error);
      alert('Failed to update registration');
    }
  };

  const openRegisterModal = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/agencies`);
      setRegisterModal({ 
        show: true, 
        mode: 'register',
        agencies: response.data, 
        selectedAgency: null,
        editRegistration: null,
        formData: {
          agency_name: '',
          address: '',
          city: '',
          country: '',
          email: '',
          phone: '',
          color_theme: '#1E40AF'
        },
        businessCard: null, 
        registrationDoc: null,
        logo: null
      });
    } catch (error) {
      console.error('Error fetching agencies:', error);
      alert('Failed to load agencies');
    }
  };

  const handleAgencySelect = (agencyId: number) => {
    const agency = registerModal.agencies.find(a => a.agency_id === agencyId);
    setRegisterModal({ 
      ...registerModal, 
      selectedAgency: agency,
      formData: {
        agency_name: agency?.agency_name || '',
        address: agency?.address || '',
        city: agency?.city || '',
        country: agency?.country || '',
        email: agency?.email || '',
        phone: agency?.phone || '',
        color_theme: agency?.color_theme || '#1E40AF'
      }
    });
  };

  const handleRegisterExisting = async () => {
    if (!registerModal.selectedAgency) {
      alert('Please select an agency');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('agency_name', registerModal.formData.agency_name);
      formData.append('address', registerModal.formData.address);
      formData.append('city', registerModal.formData.city);
      formData.append('country', registerModal.formData.country);
      formData.append('email', registerModal.formData.email);
      formData.append('phone', registerModal.formData.phone);
      formData.append('color_theme', registerModal.formData.color_theme);
      
      if (registerModal.businessCard) {
        formData.append('business_card', registerModal.businessCard);
      }
      if (registerModal.registrationDoc) {
        formData.append('registration_doc', registerModal.registrationDoc);
      }
      if (registerModal.logo) {
        formData.append('logo', registerModal.logo);
      }

      await axios.post(`${API_URL}/api/admin/register-existing-agency?agency_id=${registerModal.selectedAgency.agency_id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      alert('Agency registered successfully!');
      setRegisterModal({ 
        show: false, 
        mode: 'register',
        agencies: [], 
        selectedAgency: null,
        editRegistration: null,
        formData: {
          agency_name: '',
          address: '',
          city: '',
          country: '',
          email: '',
          phone: '',
          color_theme: '#1E40AF'
        },
        businessCard: null, 
        registrationDoc: null,
        logo: null
      });
    } catch (error: any) {
      console.error('Error registering agency:', error);
      alert(error.response?.data?.detail || 'Failed to register agency');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      disabled: 'bg-gray-100 text-gray-800'
    };
    return styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  const filteredRegistrations = registrations.filter(r => r.status === activeTab);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Agent Registrations</h1>
              <p className="text-gray-600">Review and manage agent registration requests</p>
            </div>
            <button
              onClick={openRegisterModal}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2"
            >
              <Building2 className="h-4 w-4" />
              Register Existing Agency
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'pending'
                  ? 'border-b-2 border-yellow-500 text-yellow-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Clock className="inline h-4 w-4 mr-2" />
              Pending ({registrations.filter(r => r.status === 'pending').length})
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'approved'
                  ? 'border-b-2 border-green-500 text-green-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <CheckCircle className="inline h-4 w-4 mr-2" />
              Approved ({registrations.filter(r => r.status === 'approved').length})
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'rejected'
                  ? 'border-b-2 border-red-500 text-red-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <XCircle className="inline h-4 w-4 mr-2" />
              Rejected ({registrations.filter(r => r.status === 'rejected').length})
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading registrations...</p>
          </div>
        )}

        {/* Registrations List */}
        {!loading && filteredRegistrations.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500">No {activeTab} registrations</p>
          </div>
        )}

        {!loading && filteredRegistrations.length > 0 && (
          <div className="space-y-4">
            {filteredRegistrations.map((reg) => (
              <div key={reg.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{reg.agency_name}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(reg.status)}`}>
                        {reg.status.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">Submitted {formatDate(reg.created_at)}</p>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    ID: #{reg.id}
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-start gap-2">
                    <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-sm font-medium text-gray-900">{reg.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-sm font-medium text-gray-900">{reg.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Address</p>
                      <p className="text-sm font-medium text-gray-900">{reg.address}</p>
                      {(reg.city || reg.country) && (
                        <p className="text-sm text-gray-600">{[reg.city, reg.country].filter(Boolean).join(', ')}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Documents</p>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        {reg.business_card_s3_key && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => previewDocument(reg.id, 'business_card')}
                              className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded hover:bg-green-100 flex items-center gap-1"
                            >
                              <Eye className="h-3 w-3" />
                              Preview
                            </button>
                            <button
                              onClick={() => downloadDocument(reg.id, 'business_card')}
                              className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100 flex items-center gap-1"
                            >
                              <Download className="h-3 w-3" />
                              Download
                            </button>
                          </div>
                        )}
                        {reg.registration_doc_s3_key && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => previewDocument(reg.id, 'registration_doc')}
                              className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded hover:bg-green-100 flex items-center gap-1"
                            >
                              <Eye className="h-3 w-3" />
                              Preview
                            </button>
                            <button
                              onClick={() => downloadDocument(reg.id, 'registration_doc')}
                              className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100 flex items-center gap-1"
                            >
                              <Download className="h-3 w-3" />
                              Download
                            </button>
                          </div>
                        )}
                        {reg.logo_s3_key && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => previewDocument(reg.id, 'logo')}
                              className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded hover:bg-green-100 flex items-center gap-1"
                            >
                              <Eye className="h-3 w-3" />
                              Preview
                            </button>
                            <button
                              onClick={() => downloadDocument(reg.id, 'logo')}
                              className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100 flex items-center gap-1"
                            >
                              <Download className="h-3 w-3" />
                              Download
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {reg.color_theme && (
                    <div className="flex items-start gap-2">
                      <div className="h-5 w-5 rounded mt-0.5" style={{ backgroundColor: reg.color_theme }}></div>
                      <div>
                        <p className="text-xs text-gray-500">Brand Color</p>
                        <p className="text-sm font-medium text-gray-900">{reg.color_theme}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Rejection Reason */}
                {reg.rejection_reason && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                    <p className="text-xs text-red-600 font-medium mb-1">Rejection Reason:</p>
                    <p className="text-sm text-red-800">{reg.rejection_reason}</p>
                  </div>
                )}

                {/* Review Info */}
                {reg.reviewed_at && (
                  <div className="mb-4 text-xs text-gray-500">
                    Reviewed on {formatDate(reg.reviewed_at)}
                  </div>
                )}

                {/* Actions (Pending Only) */}
                {reg.status === 'pending' && (
                  <div className="flex gap-3 pt-4 border-t">
                    <button
                      onClick={() => handleApprove(reg.id)}
                      className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Approve
                    </button>
                    <button
                      onClick={() => setRejectModal({ show: true, id: reg.id, reason: '' })}
                      className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-medium flex items-center justify-center gap-2"
                    >
                      <XCircle className="h-4 w-4" />
                      Reject
                    </button>
                  </div>
                )}

                {/* Actions (Approved/Disabled Only) */}
                {(reg.status === 'approved' || reg.status === 'disabled') && (
                  <div className="flex gap-3 pt-4 border-t">
                    <button
                      onClick={() => handleEdit(reg.id)}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Edit
                    </button>
                    {reg.status === 'approved' ? (
                      <button
                        onClick={() => handleDisable(reg.id)}
                        className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-medium flex items-center justify-center gap-2"
                      >
                        <XCircle className="h-4 w-4" />
                        Disable
                      </button>
                    ) : (
                      <button
                        onClick={() => handleEnable(reg.id)}
                        className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Enable
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {rejectModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Reject Registration</h3>
            <p className="text-sm text-gray-600 mb-4">Please provide a reason for rejection:</p>
            <textarea
              value={rejectModal.reason}
              onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={4}
              placeholder="Enter rejection reason..."
            />
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setRejectModal({ show: false, id: null, reason: '' })}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}


      {registerModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowAgencyDropdown(false)}>
          <div className="bg-white rounded-lg max-w-6xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-gray-900 mb-6">{registerModal.mode === 'edit' ? 'Edit Registration' : 'Register Existing Agency'}</h3>
            
            {/* Agency Selection - Only for register mode */}
            {registerModal.mode === 'register' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Agency</label>
                <div className="relative">
                  <input
                    type="text"
                    value={registerModal.selectedAgency ? `${registerModal.selectedAgency.agency_name}${registerModal.selectedAgency.email ? ` - ${registerModal.selectedAgency.email}` : ''}` : agencySearch}
                    onChange={(e) => {
                      setAgencySearch(e.target.value);
                      if (registerModal.selectedAgency) {
                        setRegisterModal({ ...registerModal, selectedAgency: null });
                      }
                      setShowAgencyDropdown(true);
                    }}
                    onFocus={() => setShowAgencyDropdown(true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Search agencies..."
                  />
                  {showAgencyDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredAgencies.length > 0 ? (
                        filteredAgencies.map((agency) => (
                          <div
                            key={agency.agency_id}
                            onClick={() => {
                              handleAgencySelect(agency.agency_id);
                              setShowAgencyDropdown(false);
                              setAgencySearch('');
                            }}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{agency.agency_name}</div>
                            {agency.email && <div className="text-sm text-gray-600">{agency.email}</div>}
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-gray-500 text-sm">No agencies found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Agency Details (Editable Form) */}
            <div className="space-y-4 mb-4">
              <h4 className="font-medium text-gray-900">Agency Information</h4>
              
              <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agency Name *</label>
                    <input
                      type="text"
                      value={registerModal.formData.agency_name}
                      onChange={(e) => setRegisterModal({
                        ...registerModal,
                        formData: { ...registerModal.formData, agency_name: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                    <textarea
                      value={registerModal.formData.address}
                      onChange={(e) => setRegisterModal({
                        ...registerModal,
                        formData: { ...registerModal.formData, address: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows={4}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={registerModal.formData.city}
                      onChange={(e) => setRegisterModal({
                        ...registerModal,
                        formData: { ...registerModal.formData, city: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <input
                      type="text"
                      value={registerModal.formData.country}
                      onChange={(e) => setRegisterModal({
                        ...registerModal,
                        formData: { ...registerModal.formData, country: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={registerModal.formData.email}
                      onChange={(e) => setRegisterModal({
                        ...registerModal,
                        formData: { ...registerModal.formData, email: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      type="tel"
                      value={registerModal.formData.phone}
                      onChange={(e) => setRegisterModal({
                        ...registerModal,
                        formData: { ...registerModal.formData, phone: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Brand Color Theme</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={registerModal.formData.color_theme}
                        onChange={(e) => setRegisterModal({
                          ...registerModal,
                          formData: { ...registerModal.formData, color_theme: e.target.value }
                        })}
                        className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={registerModal.formData.color_theme}
                        onChange={(e) => setRegisterModal({
                          ...registerModal,
                          formData: { ...registerModal.formData, color_theme: e.target.value }
                        })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="#1E40AF"
                        pattern="^#[0-9A-Fa-f]{6}$"
                      />
                    </div>
                  </div>
                </div>
              </div>

            {/* Existing Documents - Only for edit mode */}
            {registerModal.mode === 'edit' && registerModal.editRegistration && (
              <div className="space-y-4 mb-4">
                <h4 className="font-medium text-gray-900">Documents</h4>
                <div className="grid grid-cols-3 gap-4">
                  {registerModal.editRegistration.business_card_s3_key && (
                    <div className="border rounded-lg p-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Business Card</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => previewDocument(registerModal.editRegistration!.id, 'business_card')}
                          className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded hover:bg-green-100"
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => downloadDocument(registerModal.editRegistration!.id, 'business_card')}
                          className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100"
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  )}
                  {registerModal.editRegistration.registration_doc_s3_key && (
                    <div className="border rounded-lg p-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Registration Doc</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => previewDocument(registerModal.editRegistration!.id, 'registration_doc')}
                          className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded hover:bg-green-100"
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => downloadDocument(registerModal.editRegistration!.id, 'registration_doc')}
                          className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100"
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  )}
                  {registerModal.editRegistration.logo_s3_key && (
                    <div className="border rounded-lg p-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Logo</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => previewDocument(registerModal.editRegistration!.id, 'logo')}
                          className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded hover:bg-green-100"
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => downloadDocument(registerModal.editRegistration!.id, 'logo')}
                          className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100"
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* File Uploads - Only for register mode */}
            {registerModal.mode === 'register' && (
              <div className="space-y-4 mb-4">
                <h4 className="font-medium text-gray-900">Documents & Branding</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Agency Logo (PNG)
                    </label>
                    <input
                      type="file"
                      accept="image/png"
                      onChange={(e) => setRegisterModal({ 
                        ...registerModal, 
                        logo: e.target.files?.[0] || null 
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    {registerModal.logo && (
                      <p className="text-sm text-green-600 mt-1">✓ {registerModal.logo.name}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Card (Optional)
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setRegisterModal({ 
                        ...registerModal, 
                        businessCard: e.target.files?.[0] || null 
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    {registerModal.businessCard && (
                      <p className="text-sm text-green-600 mt-1">✓ {registerModal.businessCard.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Registration Document (Optional)
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,image/*"
                      onChange={(e) => setRegisterModal({ 
                        ...registerModal, 
                        registrationDoc: e.target.files?.[0] || null 
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    {registerModal.registrationDoc && (
                      <p className="text-sm text-green-600 mt-1">✓ {registerModal.registrationDoc.name}</p>
                    )}
                  </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setRegisterModal({ 
                  show: false, 
                  mode: 'register',
                  agencies: [], 
                  selectedAgency: null,
                  editRegistration: null,
                  formData: {
                    agency_name: '',
                    address: '',
                    city: '',
                    country: '',
                    email: '',
                    phone: '',
                    color_theme: '#1E40AF'
                  },
                  businessCard: null, 
                  registrationDoc: null,
                  logo: null
                })}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={registerModal.mode === 'edit' ? handleUpdateRegistration : handleRegisterExisting}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {registerModal.mode === 'edit' ? 'Update' : 'Register Agency'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      <FileViewerModal
        isOpen={fileViewer.isOpen}
        onClose={() => setFileViewer({ ...fileViewer, isOpen: false })}
        fileUrl={fileViewer.fileUrl}
        fileName={fileViewer.fileName}
        fileType={fileViewer.fileType}
      />
    </div>
  );
};

export default AgentRegistrations;
